import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  AccountProductEnum,
  AccountTypeEnum,
  ExternalPlaidAccount,
  IAccountDetailItem,
  InternalAdvanceAccount,
  InternalChargeCardAccount,
  InternalCheckingAccount,
  InternalSavingsAccount,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useGlobalSearchParams,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { isExternalPlaidAccount } from 'features/accounts/helpers/Account.helpers';
import useAccountDetails from 'features/accounts/hooks/useAccountDetails';
import useDeleteExternalAccount from 'features/accounts/hooks/useDeleteExternalAccount';
import AccountNumbers from 'features/accounts/ManageAccount/AccountNumbers';
import ManageAccountItems from 'features/accounts/ManageAccount/ManageAccountItems';
import ManageAccountNickname from 'features/accounts/ManageAccount/ManageAccountNickname/ManageAccountNickname';
import ManageCardListItem from 'features/accounts/ManageAccount/ManageCardListItem';
import StatementsListItem from 'features/accounts/ManageAccount/StatementsListItem';
import { EXTERNAL_ACCOUNTS } from 'features/myVaro/constants/constants';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import Dialog from 'shared/components/vxl/Dialog/Dialog';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

const SNACKBAR_DURATION = 3000;

const DividerWithWrapper = () => (
  <View style={styles.dividerWrapper}>
    <HairLineDivider />
  </View>
);

const ManageAccount = () => {
  const router = useRouter();
  const { accountId, type } = useLocalSearchParams<{
    accountId: string;
    type: AccountTypeEnum;
  }>();
  const externalAccountsUrl = usePathname();
  const insets = useSafeAreaInsets();

  const isRouteManageExternal =
    externalAccountsUrl?.includes(EXTERNAL_ACCOUNTS);

  const { id: externalAccountId } = useGlobalSearchParams<{
    id: string;
  }>();

  const { t: translatedText } = useTranslation(['accounts', 'myVaro']);
  const { isFeatureEnabled } = useStatsig();

  // info icon dialogs hooks
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const removeAccountSheetRef = React.useRef<BottomSheetModal>(null);
  const isRemovingAccountRef = React.useRef(false);
  const [showSnackBar, setShowSnackBar] = useState(false);

  const {
    removeAccountErrorModel,
    removeAccountLoading,
    removeExternalAccount,
  } = useDeleteExternalAccount({
    setDialogVisible,
    accountId,
    isRouteManageExternal,
    externalAccountId: externalAccountId as string,
  });
  const { accountDetails, errorModel, loading } = useAccountDetails(
    isRouteManageExternal && externalAccountId ? externalAccountId : accountId,
  );

  let removeExternalAccountMessage = '';
  if (isExternalPlaidAccount(accountDetails)) {
    removeExternalAccountMessage = accountDetails.isCraEnabled
      ? translatedText(
          'manageAccount.bottomSheet.removeExternalAccountMessageCRA',
        )
      : translatedText(
          'manageAccount.bottomSheet.removeExternalAccountMessageNonCRA',
        );
  }

  const getAccountType = (
    accountDetails:
      | ExternalPlaidAccount
      | InternalCheckingAccount
      | InternalSavingsAccount
      | InternalAdvanceAccount
      | InternalChargeCardAccount
      | undefined,
  ) =>
    accountDetails?.type === AccountTypeEnum.External
      ? accountDetails.type.toLowerCase()
      : accountDetails?.product.toLowerCase();

  useViewDidFocusScreenAnalyticsEvent(
    accountDetails
      ? {
          key: 'manageAccountsPage',
          customAttributes:
            accountDetails.product === AccountProductEnum.ChargeCard
              ? {
                  category: getAccountType(accountDetails) as string,
                  subCategory: 'believe_management',
                }
              : {
                  category: getAccountType(accountDetails) as string,
                },
        }
      : undefined,
  );

  const filteredItems: IAccountDetailItem[] =
    accountDetails?.details?.flatMap((item) => {
      return item.items.filter(
        (value) =>
          value.key ===
          translatedText('manageAccount.chargeCardAvailableToSpend'),
      );
    }) || [];

  useAutoHideSnackbar(
    Boolean(showSnackBar),
    () => {
      setShowSnackBar(false);
    },
    SNACKBAR_DURATION,
  );

  /**
   * launch effect to retrieve accountR2 query with account info
   */
  const internalAccount = accountDetails as
    InternalCheckingAccount | InternalChargeCardAccount;

  const cardData = internalAccount?.cards?.[0];
  const shouldShowPayOverTimeEntryPoint =
    accountDetails?.product === AccountProductEnum.Checking &&
    isFeatureEnabled(FeatureFlagKey.ENABLE_PAY_OVER_TIME);

  const okButton = [
    {
      label: translatedText('manageAccount.dialog.buttons.ok'),
      onClick: () => {
        setDialogVisible(false);
      },
    },
  ];

  const onPressManageAccountItem = (item: IAccountDetailItem) => {
    if (!item) {
      return;
    }
    const showDialog =
      !!item.description && !item.itemDetail && !item.articleLink;
    if (showDialog) {
      setDialogMessage(item.description ?? '');
      setDialogVisible(true);
      return;
    }
    if (item.key === 'savings_tracker_apy') {
      return router.push({
        pathname: '/accounts/savings-tracker',
        params: { id: accountId },
      });
    }
    if (item.key === 'apy') {
      return router.push({
        pathname: '/accounts/current-apy',
        params: { id: accountId },
      });
    }
  };

  return (
    <View style={[styles.parentContainer, { paddingBottom: insets.bottom }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText(
            isRouteManageExternal || type === AccountTypeEnum.External
              ? 'myVaro:externalAccounts.navigationTitle'
              : 'manageAccount.buttons.accountSettingLabel',
          ),
          header: (props: NativeStackHeaderProps) => {
            return <ExpoNavigationBar {...props} />;
          },
        }}
      />
      <ScrollView
        style={styles.parentContainer}
        contentContainerStyle={styles.scrollContainer}
      >
        <View style={styles.parentContainer}>
          <SectionDivider
            labelText={translatedText('manageAccount.information')}
          />
          <View style={styles.accountContainer}>
            <ManageAccountNickname
              accountId={accountDetails?.id}
              label={translatedText('manageAccount.accountName')}
              nickname={accountDetails?.nickname}
              isEditable={
                accountDetails?.type !== AccountTypeEnum.External &&
                (accountDetails?.product === AccountProductEnum.Checking ||
                  accountDetails?.product === AccountProductEnum.Savings)
              }
              accountType={accountDetails?.product as AccountProductEnum}
            />
            {accountDetails?.fullAccountNumber ? (
              <>
                <View style={styles.divider} />
                <AccountNumbers
                  accountDetails={accountDetails}
                  onCopyPress={() => setShowSnackBar(true)}
                />
              </>
            ) : null}
            {(accountDetails as InternalChargeCardAccount)?.isSafePayEnabled ? (
              // JT-120965-Details should be filtered from gql side
              <>
                <View style={styles.divider} />
                <ListItem
                  key={filteredItems[0]?.key}
                  primaryCopy={filteredItems[0]?.value}
                  secondaryCopy={filteredItems[0]?.title}
                  lineFormat={ListItemLineFormat.LABEL}
                  showsChevron={false}
                  onPress={() => onPressManageAccountItem(filteredItems[0])}
                  trailingAccessory={{
                    id: '1',
                    name: VXLComponentId.ICON,
                    props: {
                      iconKey: 'QUESTION_MARK',
                      iconAccessibilityLabel: 'Help icon',
                    },
                  }}
                />
              </>
            ) : (
              <>
                <View style={styles.divider} />
                <ManageAccountItems
                  items={accountDetails?.details ? accountDetails?.details : []}
                  onPress={onPressManageAccountItem}
                  accountType={getAccountType(accountDetails) as string}
                />
              </>
            )}

            {accountDetails?.type !== AccountTypeEnum.External ? (
              <View>
                <View style={styles.divider} />
                <SectionDivider
                  labelText={translatedText(
                    'manageAccount.buttons.manageAccountLabel',
                  )}
                />
                {accountDetails?.product === AccountProductEnum.ChargeCard ? (
                  <View style={styles.accountContainer}>
                    <View>
                      <ListItem
                        primaryCopy={translatedText('manageAccount.safePay')}
                        onPress={() => {
                          router.push({
                            pathname: `/accounts/[id]/safe-credit-builder`,
                            params: { id: accountId },
                          });
                        }}
                        trailingAccessory={{
                          id: 'account-toggle-switch',
                          name: VXLComponentId.TAG,
                          props: {
                            type: (accountDetails as InternalChargeCardAccount)
                              ?.isSafePayEnabled
                              ? TagType.SUCCESS
                              : TagType.ERROR,
                            text: (accountDetails as InternalChargeCardAccount)
                              ?.isSafePayEnabled
                              ? translatedText('accountPageTiles.tagStatus.on')
                              : translatedText(
                                  'accountPageTiles.tagStatus.off',
                                ),
                          },
                        }}
                      />
                      <DividerWithWrapper />
                      {(accountDetails as InternalChargeCardAccount)
                        ?.isSafePayEnabled ? (
                        <>
                          <ListItem
                            primaryCopy={translatedText(
                              'manageAccount.creditBuildingDetails',
                            )}
                            analyticsId="creditBuildingClick"
                            customAttributes={{
                              faq_name: 'credit_building_details',
                            }}
                            onPress={() => {
                              router.push({
                                pathname: '/believe/credit-building/[id]',
                                params: { id: accountId },
                              });
                            }}
                          />
                          <DividerWithWrapper />
                        </>
                      ) : null}
                      {internalAccount?.canAccessCardService ? (
                        <ManageCardListItem
                          title={translatedText('manageAccount.manageCard')}
                          cardId={cardData?.id}
                          cardType={cardData?.type}
                          accountType={internalAccount.product}
                        />
                      ) : null}
                      {!(accountDetails as InternalChargeCardAccount)
                        ?.isSafePayEnabled ? (
                        <>
                          <ListItem
                            primaryCopy={translatedText(
                              'manageAccount.paymentHistory',
                            )}
                            analyticsId="paymentHistoryClick"
                            onPress={() => {
                              router.push({
                                pathname: `/believe/payment-history`,
                                params: { id: accountId },
                              });
                            }}
                            customAttributes={{
                              account_type: 'charge_card',
                            }}
                          />
                          <DividerWithWrapper />
                        </>
                      ) : null}
                      <StatementsListItem
                        title={translatedText(
                          'manageAccount.statementsAndDocuments',
                        )}
                        accountId={accountId}
                        analyticsId="statementAndDocumentClick"
                        accountType={getAccountType(accountDetails) as string}
                      />
                    </View>
                    <View>
                      <View style={styles.divider} />
                      <SectionDivider
                        labelText={translatedText('manageAccount.help')}
                      />
                      <ListItem
                        primaryCopy={translatedText('manageAccount.cardBasics')}
                        analyticsId="cardBasics"
                        customAttributes={{
                          faq_name: 'believe_intro',
                        }}
                        onPress={() => {
                          router.push({
                            pathname: '/believe/how-it-works',
                            params: { accountId: accountId },
                          });
                        }}
                      />
                    </View>
                  </View>
                ) : (
                  <>
                    {internalAccount?.canAccessCardService ? (
                      <ManageCardListItem
                        title={translatedText('manageAccount.manageCard')}
                        cardId={cardData?.id}
                        cardType={cardData?.type}
                        accountType={internalAccount.product}
                      />
                    ) : null}
                    <StatementsListItem
                      title={translatedText(
                        'manageAccount.statementsAndDocuments',
                      )}
                      accountId={accountId}
                      analyticsId="manageAccountStatementsAndDocumentsClicked"
                      accountType={getAccountType(accountDetails)}
                    />
                    {shouldShowPayOverTimeEntryPoint ? (
                      <>
                        <DividerWithWrapper />
                        <ListItem
                          primaryCopy={translatedText(
                            'manageAccount.payOverTime',
                          )}
                          onPress={() => {
                            router.push({
                              pathname: '/pay-over-time/manage-loans',
                            });
                          }}
                          analyticsId="manageAccountPayOverTimeClicked"
                        />
                      </>
                    ) : null}
                  </>
                )}
              </View>
            ) : null}
          </View>
        </View>
        {accountDetails?.type === AccountTypeEnum.External ? (
          <View style={styles.buttonContainer}>
            <Button
              label={translatedText('manageAccount.buttons.removeAccount')}
              type="SECONDARY"
              analyticsId="removeAccountClick"
              onPress={() => {
                removeAccountSheetRef.current?.present();
              }}
            />
          </View>
        ) : null}

        <Dialog
          titleText=""
          messageText={dialogMessage}
          isDialogVisible={dialogVisible}
          setModalVisible={setDialogVisible}
          dialogButtonProps={okButton}
        />

        {/* Remove Account BottomSheet */}
        <BottomSheet
          ref={removeAccountSheetRef}
          showOverlay
          onDismiss={() => {
            if (isRemovingAccountRef.current) {
              isRemovingAccountRef.current = false;
              void removeExternalAccount();
            }
          }}
        >
          <View
            testID="manage-account-remove-account-bottom-sheet"
            style={styles.bottomSheetContent}
          >
            <TitleMdText style={styles.bottomSheetTitle}>
              {translatedText('manageAccount.bottomSheet.bottomsheetTitle')}
            </TitleMdText>
            <BodyBaseText>{removeExternalAccountMessage}</BodyBaseText>
            <View style={styles.spacer} />
            <View style={styles.buttonGroup}>
              <Button
                label={translatedText('manageAccount.buttons.removeAccount')}
                type="PRIMARY"
                onPress={() => {
                  isRemovingAccountRef.current = true;
                  removeAccountSheetRef.current?.dismiss();
                }}
                size="LARGE"
                width="FULL"
              />
              <Button
                label={translatedText('manageAccount.dialog.buttons.cancel')}
                type="SECONDARY"
                onPress={async () => {
                  removeAccountSheetRef.current?.dismiss();
                }}
                size="LARGE"
                width="FULL"
              />
            </View>
          </View>
        </BottomSheet>
      </ScrollView>
      {showSnackBar ? (
        <Snackbar
          text={translatedText('manageAccount.copiedText')}
          duration={SNACKBAR_DURATION}
        />
      ) : null}
      <ScreenTransitionOverlay
        isLoading={removeAccountLoading || loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          removeAccountErrorModel || errorModel ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              {...(removeAccountErrorModel && { ...removeAccountErrorModel })}
              {...(errorModel && { ...errorModel })}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: theme.spacing.l,
  },
  buttonGroup: {
    gap: theme.spacing.m,
  },
  bottomSheetContent: {
    paddingHorizontal: theme.spacing.l,
    paddingBottom: theme.spacing.l,
  },
  bottomSheetTitle: {
    marginBottom: theme.spacing.s,
  },
  accountContainer: {
    flex: 1,
    gap: theme.spacing.s,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    padding: theme.spacing.m,
  },
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  dividerWrapper: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
  parentContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
});

export default ManageAccount;
